import { useEffect, useMemo, useState } from 'react' 
import { useNavigate } from 'react-router-dom'
import { HorizontalLoader, ModalWrapper } from 'Components'
import { handleError, handleSuccess } from 'Helpers' 
import {
  MarkAllPendingPaidPayload,
  MarkSinglePendingPaidPayload,
  PendingPayoutUserXModel,
  PayoutHistoryParams,
  PayoutHistoryXModel,
  PayoutQueueParams,
} from 'BoostXpressAdmin/Model/payoutXModel'
import { payoutBXService } from 'BoostXpressAdmin/Services/payoutBXService'
import { referralBXService } from 'BoostXpressAdmin/Services/referralBXService'
import {
  StationAttendantsStats,
  stationAttendantsBXService,
} from 'BoostXpressAdmin/Services/stationAttendantsBXService'
import { ToastContainer } from 'react-toastify'
import './Payouts.scss'
import PayoutQueueTable from './PayoutQueueTable' 
import PayoutHistoryTable from './PayoutHistoryTable'

const formatMajorNaira = (value: number) => {
  const formatted = new Intl.NumberFormat('en-NG', { maximumFractionDigits: 0 }).format(value ?? 0)
  return `₦${formatted}`
}

const PAGE_SIZE_QUEUE = 20
const PAGE_SIZE_HISTORY = 25
 
const extractPagination = (
  meta: any,
  count: number,
  fallbackPage: number,
  fallbackLimit: number,
) => {
  const pagination = meta?.pagination ?? meta?.data?.pagination ?? null
  return {
    total: Number(pagination?.total ?? count),
    perPage: Number(pagination?.per_page ?? pagination?.limit ?? fallbackLimit),
    currentPage: Number(pagination?.current_page ?? pagination?.page ?? fallbackPage),
  }
}

 
const fullName = (firstName?: string | null, lastName?: string | null) =>
  `${firstName ?? ''} ${lastName ?? ''}`.trim() || '—'

const Payouts = () => { 
 
  const [activeTab, setActiveTab] = useState<'queue' | 'history'>('queue')

  const [queueRows, setQueueRows] = useState<PendingPayoutUserXModel[]>([])
  const [isLoadingQueue, setIsLoadingQueue] = useState(false)
  const [queuePage, setQueuePage] = useState(1)
  const [queueTotal, setQueueTotal] = useState(0)
  const [queueLimit] = useState(PAGE_SIZE_QUEUE)
  const [queueSearch, setQueueSearch] = useState('')
  // const [queueCountry, setQueueCountry] = useState('Nigeria')
  const [queueIncludeWithoutBankInfo, setQueueIncludeWithoutBankInfo] = useState(false)
  const [queueSortOrder, setQueueSortOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [showQueueFilters, setShowQueueFilters] = useState(false)

  const [historyRows, setHistoryRows] = useState<PayoutHistoryXModel[]>([])
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [historyPage, setHistoryPage] = useState(1)
  const [historyTotal, setHistoryTotal] = useState(0)
  const [historyLimit] = useState(PAGE_SIZE_HISTORY)
  const [historySearch, setHistorySearch] = useState('')
  const [historyStatus, setHistoryStatus] = useState('')
  const [historyUserId, setHistoryUserId] = useState('')
  const [historyDateFrom, setHistoryDateFrom] = useState('')
  const [historyDateTo, setHistoryDateTo] = useState('')
  const [historyMinAmountMajor, setHistoryMinAmountMajor] = useState('')
  const [historyMaxAmountMajor, setHistoryMaxAmountMajor] = useState('')
  const [historySortOrder, setHistorySortOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [showHistoryFilters, setShowHistoryFilters] = useState(false)

  const [showMarkAllModal, setShowMarkAllModal] = useState(false)
  const [markAllPayload, setMarkAllPayload] = useState<MarkAllPendingPaidPayload>({
    includeWithoutBankInfo: true,
    adminNote: '',
  })
  const [isSubmittingMarkAll, setIsSubmittingMarkAll] = useState(false)

  const [showMarkSingleModal, setShowMarkSingleModal] = useState(false)
  const [selectedPendingUser, setSelectedPendingUser] = useState<PendingPayoutUserXModel | null>(
    null,
  )
  const [markSinglePayload, setMarkSinglePayload] = useState<MarkSinglePendingPaidPayload>({
    amountMajor: 0,
    adminNote: '',
  })
  const [isSubmittingMarkSingle, setIsSubmittingMarkSingle] = useState(false)
 

  const loadQueue = async () => {
    try {
      setIsLoadingQueue(true)
      const params: PayoutQueueParams = {
        page: queuePage,
        limit: queueLimit,
        search: queueSearch.trim() || undefined,
        // country: queueCountry.trim() || undefined,
        includeWithoutBankInfo: queueIncludeWithoutBankInfo,
        sortOrder: queueSortOrder,
      }

      const res = await payoutBXService.pendingPayouts(params)
      // console.log({ res })
      const data = Array.isArray(res.data?.data) ? res.data.data : []
      const pagination = extractPagination(res.data?.meta, data.length, queuePage, queueLimit)

      setQueueRows(data)
      setQueueTotal(pagination.total)
    } catch (e) {
      setQueueRows([])
      setQueueTotal(0)
      handleError({ response: { data: { error: 'Failed to fetch payout queue.' } } })
    } finally {
      setIsLoadingQueue(false)
    }
  }

 

  const loadHistory = async () => {
    try {
      setIsLoadingHistory(true)
      const params: PayoutHistoryParams = {
        page: historyPage,
        limit: historyLimit,
        status: historyStatus || undefined,
        userId: historyUserId.trim() || undefined,
        search: historySearch.trim() || undefined,
        dateFrom: historyDateFrom || undefined,
        dateTo: historyDateTo || undefined,
        minAmountMajor: historyMinAmountMajor ? Number(historyMinAmountMajor) : undefined,
        maxAmountMajor: historyMaxAmountMajor ? Number(historyMaxAmountMajor) : undefined,
        sortOrder: historySortOrder,
      }

      const res = await payoutBXService.payouts(params)
      const data = Array.isArray(res.data?.data) ? res.data.data : []
      const pagination = extractPagination(res.data?.meta, data.length, historyPage, historyLimit)

      setHistoryRows(data)
      setHistoryTotal(pagination.total)
    } catch (e) {
      setHistoryRows([])
      setHistoryTotal(0)
      handleError({ response: { data: { error: 'Failed to fetch payout history.' } } })
    } finally {
      setIsLoadingHistory(false)
    }
  }

  

  useEffect(() => {
    loadQueue()
  }, [
    queuePage,
    queueLimit,
    queueSearch,
    // queueCountry,
    queueIncludeWithoutBankInfo,
    queueSortOrder,
  ])

  useEffect(() => {
    loadHistory()
  }, [
    historyPage,
    historyLimit,
    historySearch,
    historyStatus,
    historyUserId,
    historyDateFrom,
    historyDateTo,
    historyMinAmountMajor,
    historyMaxAmountMajor,
    historySortOrder,
  ])
 
  const currentMonthLabel = useMemo(
    () => new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
    [],
  )

  const queueAmountVisible = useMemo(
    () => queueRows.reduce((sum, item) => sum + Number(item.availableMajor ?? 0), 0),
    [queueRows],
  )

  const historyAmountVisible = useMemo(
    () =>
      historyRows
        .filter((item) => item.status === 'COMPLETED')
        .reduce((sum, item) => sum + Number(item.amountMajor ?? 0), 0),
    [historyRows],
  )

 

  const openSinglePayModal = (row: PendingPayoutUserXModel) => {
    setSelectedPendingUser(row)
    setMarkSinglePayload({
      amountMajor: Number(row.availableMajor ?? 0),
      adminNote: '',
    })
    setShowMarkSingleModal(true)
  }

  const submitMarkAllPaid = async () => {
    try {
      setIsSubmittingMarkAll(true)
      await payoutBXService.markAllPaid(markAllPayload)
      handleSuccess('All pending payouts have been marked as paid.')
      setShowMarkAllModal(false)
      await Promise.all([loadQueue(), loadHistory() ])
    } catch (error) {
      handleError(error as any)
    } finally {
      setIsSubmittingMarkAll(false)
    }
  }

  const submitMarkSinglePaid = async () => {
    if (!selectedPendingUser?.resourceId) return

    try {
      setIsSubmittingMarkSingle(true)
      await payoutBXService.markSinglePaid(selectedPendingUser.resourceId, markSinglePayload)
      handleSuccess('Payout marked as paid successfully.')
      setShowMarkSingleModal(false)
      setSelectedPendingUser(null)
      await Promise.all([loadQueue(), loadHistory() ])
    } catch (error) {
      handleError(error as any)
    } finally {
      setIsSubmittingMarkSingle(false)
    }
  }

 
 

  return (
    <div className='bx-payouts-page'>
      <ToastContainer />

      <section className='bx-payout-board'>
        <div className='bx-payout-board__header'>
          <div className='bx-payouts-page__header'>
            <h2 className='bx-payouts-page__title'>Payout queue and full history</h2>
          </div>
          <button
            type='button'
            className='bx-payout-board__pay-all'
            onClick={() => setShowMarkAllModal(true)}
            disabled={isLoadingQueue || !queueRows.length}
          >
            Pay All Pending ({formatMajorNaira(queueAmountVisible)})
          </button>
        </div>

        <div className='row g-3'>
          <div className='col-12 col-lg-6'>
            <div className='bx-payout-board__summary-card'>
              <span className='bx-payout-board__summary-label'>Total Pending</span>
              <strong className='bx-payout-board__summary-value'>
                {isLoadingQueue ? 'Loading...' : formatMajorNaira(queueAmountVisible)}
              </strong>
              <span className='bx-payout-board__summary-sub'>
                {isLoadingQueue
                  ? 'Fetching queue...'
                  : `${queueRows.length} attendants in visible queue`}
              </span>
            </div>
          </div>

          <div className='col-12 col-lg-6'>
            <div className='bx-payout-board__summary-card'>
              <span className='bx-payout-board__summary-label'>
                Total Paid - {currentMonthLabel}
              </span>
              <strong className='bx-payout-board__summary-value'>
                {isLoadingHistory ? 'Loading...' : formatMajorNaira(historyAmountVisible)}
              </strong>
              <span className='bx-payout-board__summary-sub'>
                {isLoadingHistory
                  ? 'Fetching history...'
                  : `${historyRows.length} payouts in visible history`}
              </span>
            </div>
          </div>
        </div>

        <div className='bx-payout-board__tabs'>
          <button
            type='button'
            className={`bx-payout-board__tab ${activeTab === 'queue' ? 'bx-payout-board__tab--active' : ''}`}
            onClick={() => setActiveTab('queue')}
          >
            Payout Queue
          </button>
          <button
            type='button'
            className={`bx-payout-board__tab ${activeTab === 'history' ? 'bx-payout-board__tab--active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Payout History
          </button>
        </div>

     

        <div className='bx-payout-board__table-shell border-0'>
          {(activeTab === 'queue' && isLoadingQueue) ||
          (activeTab === 'history' && isLoadingHistory) ? (
            <div className='bx-payout-board__loader'>
              <HorizontalLoader />
            </div>
          ) : (
            <div className='table-responsive-'>
              {activeTab === 'queue' ? (
                <PayoutQueueTable onMarkSinglePay={openSinglePayModal} />
              ) : (
                <PayoutHistoryTable />
              )}
            </div>
          )}
        </div> 
      </section>

      <ModalWrapper
        showModal={showMarkAllModal}
        closeModal={() => setShowMarkAllModal(false)}
        title='Pay All Pending'
        size='lg'
      >
        <div className='bx-payout-modal'>
          <label className='bx-payout-modal__checkbox'>
            <input
              type='checkbox'
              checked={markAllPayload.includeWithoutBankInfo}
              onChange={(e) =>
                setMarkAllPayload((current) => ({
                  ...current,
                  includeWithoutBankInfo: e.target.checked,
                }))
              }
            />
            <span>Include attendants without bank info</span>
          </label>

          <label className='bx-payout-modal__field'>
            <span>Admin Note</span>
            <textarea
              className='form-control'
              rows={4}
              value={markAllPayload.adminNote}
              onChange={(e) =>
                setMarkAllPayload((current) => ({
                  ...current,
                  adminNote: e.target.value,
                }))
              }
            />
          </label>

          <div className='bx-payout-modal__actions'>
            <button
              type='button'
              className='bx-payout-modal__secondary'
              onClick={() => setShowMarkAllModal(false)}
            >
              Cancel
            </button>
            <button
              type='button'
              className='bx-payout-modal__primary'
              disabled={isSubmittingMarkAll}
              onClick={submitMarkAllPaid}
            >
              {isSubmittingMarkAll ? 'Submitting...' : 'Confirm Payment'}
            </button>
          </div>
        </div>
      </ModalWrapper>

      <ModalWrapper
        showModal={showMarkSingleModal}
        closeModal={() => setShowMarkSingleModal(false)}
        title='Mark Single Payout as Paid'
        size='lg'
      >
        <div className='bx-payout-modal'>
          <div className='bx-payout-modal__summary'>
            <strong>
              {fullName(selectedPendingUser?.firstName, selectedPendingUser?.lastName)}
            </strong>
            <span>{selectedPendingUser?.bankInfo?.bankName || 'No bank info available'}</span>
          </div>

          <label className='bx-payout-modal__field'>
            <span>Amount (Major)</span>
            <input
              type='number'
              className='form-control'
              value={markSinglePayload.amountMajor}
              onChange={(e) =>
                setMarkSinglePayload((current) => ({
                  ...current,
                  amountMajor: Number(e.target.value),
                }))
              }
            />
          </label>

          <label className='bx-payout-modal__field'>
            <span>Admin Note</span>
            <textarea
              className='form-control'
              rows={4}
              value={markSinglePayload.adminNote}
              onChange={(e) =>
                setMarkSinglePayload((current) => ({
                  ...current,
                  adminNote: e.target.value,
                }))
              }
            />
          </label>

          <div className='bx-payout-modal__actions'>
            <button
              type='button'
              className='bx-payout-modal__secondary'
              onClick={() => setShowMarkSingleModal(false)}
            >
              Cancel
            </button>
            <button
              type='button'
              className='bx-payout-modal__primary'
              disabled={isSubmittingMarkSingle}
              onClick={submitMarkSinglePaid}
            >
              {isSubmittingMarkSingle ? 'Submitting...' : 'Mark Paid'}
            </button>
          </div>
        </div>
      </ModalWrapper>
    </div>
  )
}

export default Payouts